import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, X, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Globe, Activity, CalendarDays } from 'lucide-react';
import { Input } from '@/components/ui/input.tsx';
import {
    getLtvPageDomains, getLtvPageLeadsFlat, getLtvPageLeads, getLtvLeadTraffic, getLtvLeadDaily,
} from '@/services/api/ltv';
import type { LtvPage, LtvDomainRow, LtvLead, LtvLeadDay, SortOrder } from '@/types/ltv';
import { money, num, dec, initials, countryName, SortHeader, toggleSort, Spinner, Empty, Pagination } from './shared';

const LIMIT = 50;

// helpers de data (strings YYYY-MM-DD comparam lexicograficamente)
const addDays = (isoDate: string, n: number) => {
    const d = new Date(isoDate + 'T00:00:00');
    d.setDate(d.getDate() + n);
    return d.toISOString().slice(0, 10);
};
const minIso = (a: string, b: string) => (a < b ? a : b);

export default function PagePanel({
    page, byDomain, onToggleByDomain, domain, onPickDomain, lockedDomain, country,
    selectedLead, onSelectLead, onBack, start_date, end_date,
}: {
    page: LtvPage;
    byDomain: boolean;
    onToggleByDomain: (v: boolean) => void;
    domain: string | null;
    onPickDomain: (d: string) => void;
    lockedDomain?: string | null; // filtro de domínio do topo (trava o escopo)
    country?: string;             // filtro de país do topo (escopa o drill)
    selectedLead: LtvLead | null;
    onSelectLead: (l: LtvLead | null) => void;
    onBack: () => void;
    start_date: string;
    end_date: string;
}) {
    const pId = page.p_id;
    const effectiveDomain = lockedDomain ?? domain;
    const showDomainsTable = !lockedDomain && byDomain && domain === null;
    const showDomainCol = !byDomain && !lockedDomain; // coluna Domínio só na visão flat
    const [sortBy, setSortBy] = useState('revenue_page');
    const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
    const [q, setQ] = useState('');
    const [offset, setOffset] = useState(0);

    // reseta ordenação/busca ao trocar de modo/domínio
    useEffect(() => {
        setSortBy(!lockedDomain && byDomain && !domain ? 'revenue_page' : 'revenue_lead');
        setSortOrder('desc'); setQ(''); setOffset(0);
    }, [byDomain, domain, lockedDomain]);

    const baseParams = { start_date, end_date, country: country || undefined, sort_by: sortBy, sort_order: sortOrder, q: q || undefined, limit: LIMIT, offset };

    const domainsQ = useQuery({
        queryKey: ['ltv-domains', pId, start_date, end_date, country ?? '', sortBy, sortOrder, q, offset],
        queryFn: () => getLtvPageDomains(pId, baseParams),
        enabled: showDomainsTable,
        placeholderData: (p) => p,
    });
    const leadsQ = useQuery({
        queryKey: ['ltv-leads', pId, effectiveDomain, start_date, end_date, country ?? '', sortBy, sortOrder, q, offset],
        queryFn: () => effectiveDomain !== null ? getLtvPageLeads(pId, effectiveDomain, baseParams) : getLtvPageLeadsFlat(pId, baseParams),
        enabled: !showDomainsTable,
        placeholderData: (p) => p,
    });

    const onSort = (field: string) => {
        const s = toggleSort(field, sortBy, sortOrder);
        setSortBy(s.sort_by); setSortOrder(s.sort_order); setOffset(0);
    };

    return (
        <div className="space-y-4">
            {/* Header card da página */}
            <div className="bg-white border border-gray-200 rounded-2xl px-5 py-4 flex items-center gap-4">
                <button onClick={onBack} className="w-9 h-9 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50 shrink-0">
                    <ChevronLeft className="w-4 h-4" />
                </button>
                <div className="w-11 h-11 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-sm shrink-0">
                    {initials(page.page_name)}
                </div>
                <div className="min-w-0">
                    <p className="font-bold text-gray-900 truncate">{page.page_name || pId}</p>
                    <p className="text-xs text-gray-400 font-mono">p_id {pId}</p>
                </div>
                {lockedDomain ? (
                    <span className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-blue-200 text-blue-600 bg-blue-50">
                        <Globe className="w-3.5 h-3.5" /> {lockedDomain}
                    </span>
                ) : (
                    <button onClick={() => onToggleByDomain(!byDomain)}
                        className={`ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${byDomain ? 'border-blue-500 text-blue-600 bg-blue-50' : 'border-gray-200 text-gray-500 hover:bg-gray-50'}`}>
                        <Globe className="w-3.5 h-3.5" /> Por domínio
                    </button>
                )}
                <div className="flex items-center gap-6 pl-4 border-l border-gray-100 shrink-0">
                    <Stat label="Receita" value={money(page.revenue_page)} accent />
                    <Stat label="Pageviews" value={num(page.pv_total_page)} />
                    <Stat label="Leads (per.)" value={num(page.lead_count)} />
                    <Stat label="Leads totais" value={num(page.page_leads_total ?? 0)} />
                    <Stat label="Interação" value={`${dec(page.avg_interaction_days ?? 0)} d`} />
                </div>
            </div>

            {showDomainsTable ? (
                <DomainsTable q={domainsQ} onSort={onSort} sortBy={sortBy} sortOrder={sortOrder}
                    onPickDomain={onPickDomain} offset={offset} limit={LIMIT} onOffset={setOffset} />
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
                    <LeadsList q={leadsQ} onSort={onSort} sortBy={sortBy} sortOrder={sortOrder}
                        showDomain={showDomainCol} query={q} setQuery={(v: string) => { setQ(v); setOffset(0); }}
                        selectedLead={selectedLead} onSelectLead={onSelectLead}
                        offset={offset} limit={LIMIT} onOffset={setOffset} />
                    <LeadDetail lead={selectedLead} fallbackDomain={effectiveDomain} start_date={start_date} end_date={end_date} />
                </div>
            )}
        </div>
    );
}

function Stat({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
    return (
        <div className="text-right">
            <p className={`text-lg font-bold ${accent ? 'text-emerald-600' : 'text-gray-900'}`}>{value}</p>
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">{label}</p>
        </div>
    );
}

function CountryBadge({ code }: { code?: string | null }) {
    if (!code) return <span className="text-gray-400">—</span>;
    return <span title={code} className="inline-block px-1.5 py-0.5 rounded bg-gray-100 text-[11px] font-medium text-gray-600">{countryName(code)}</span>;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function DomainsTable({ q, onSort, sortBy, sortOrder, onPickDomain, offset, limit, onOffset }: any) {
    const rows: LtvDomainRow[] = q.data?.data ?? [];
    return (
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
            <div className="px-5 py-4">
                <h3 className="text-base font-bold text-gray-900">Domínios <span className="ml-1 text-xs font-medium text-blue-600 bg-blue-50 rounded px-1.5 py-0.5 align-middle">{q.data?.meta?.total ?? rows.length} no período</span></h3>
                <p className="text-xs text-gray-400 mt-0.5">clique para ver os leads do domínio</p>
            </div>
            {q.isLoading && !q.data ? <Spinner /> : rows.length === 0 ? <Empty label="Nenhum domínio com receita nesse período." /> : (
                <div className={`overflow-x-auto ${q.isPlaceholderData ? 'opacity-60' : ''}`}>
                    <table className="w-full text-sm min-w-[720px]">
                        <thead><tr className="border-y border-gray-100 bg-gray-50/50">
                            <SortHeader label="Domínio" field="domain" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                            <SortHeader label="Receita" field="revenue_page" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} align="right" />
                            <SortHeader label="Pageviews" field="pv_total_page" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} align="right" />
                            <SortHeader label="Impr. GAM" field="impressions_page" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} align="right" />
                            <SortHeader label="Clicks GAM" field="gam_clicks" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} align="right" />
                            <SortHeader label="Clicks" field="clicks" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} align="right" />
                            <SortHeader label="Leads" field="lead_count" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} align="right" />
                            <SortHeader label="eCPM" field="ecpm_page" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} align="right" />
                        </tr></thead>
                        <tbody className="divide-y divide-gray-100">
                            {rows.map((r) => (
                                <tr key={r.domain} onClick={() => onPickDomain(r.domain)} className="cursor-pointer hover:bg-gray-50/60 transition-colors">
                                    <td className="px-4 py-3 font-medium text-gray-900"><span className="flex items-center gap-1.5"><ChevronRight className="w-3.5 h-3.5 text-gray-300" />{r.domain}</span></td>
                                    <td className="px-4 py-3 text-right font-semibold text-emerald-600">{money(r.revenue_page)}</td>
                                    <td className="px-4 py-3 text-right text-gray-600">{num(r.pv_total_page)}</td>
                                    <td className="px-4 py-3 text-right text-gray-600">{num(r.impressions_page)}</td>
                                    <td className="px-4 py-3 text-right text-gray-600">{num(r.gam_clicks ?? 0)}</td>
                                    <td className="px-4 py-3 text-right text-gray-600">{num(r.clicks)}</td>
                                    <td className="px-4 py-3 text-right text-gray-600">{num(r.lead_count)}</td>
                                    <td className="px-4 py-3 text-right text-gray-500">{dec(r.ecpm_page)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div className="px-4 pb-3"><Pagination total={q.data?.meta?.total ?? 0} limit={limit} offset={offset} onOffset={onOffset} /></div>
                </div>
            )}
        </div>
    );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function LeadsList({ q, onSort, sortBy, sortOrder, showDomain, query, setQuery, selectedLead, onSelectLead, offset, limit, onOffset }: any) {
    const rows: LtvLead[] = q.data?.data ?? [];
    const total = q.data?.meta?.total ?? rows.length;
    const selKey = selectedLead ? `${selectedLead.domain ?? ''}-${selectedLead.lead_id}` : null;
    return (
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
            <div className="flex items-center justify-between gap-3 px-5 py-4">
                <h3 className="text-base font-bold text-gray-900 whitespace-nowrap">Leads <span className="ml-1 text-xs font-medium text-blue-600 bg-blue-50 rounded px-1.5 py-0.5 align-middle">{total} no período</span></h3>
                <div className="relative w-56">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-300" />
                    <Input value={query} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)} placeholder="Buscar por nome..." className="pl-9 h-9 rounded-lg border-gray-200 text-sm bg-gray-50 focus:bg-white" />
                    {query && <button onClick={() => setQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"><X className="w-3.5 h-3.5" /></button>}
                </div>
            </div>
            {q.isLoading && !q.data ? <Spinner /> : rows.length === 0 ? <Empty label="Nenhum lead nesse período." /> : (
                <div className={`overflow-x-auto ${q.isPlaceholderData ? 'opacity-60' : ''}`}>
                    <table className="w-full text-sm">
                        <thead><tr className="border-y border-gray-100 bg-gray-50/50">
                            <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Lead</th>
                            <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wide">País</th>
                            {showDomain && <th className="px-4 py-3 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Domínio</th>}
                            <SortHeader label="Receita" field="revenue_lead" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} align="right" />
                            <SortHeader label="Clicks" field="clicks" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} align="right" />
                            <SortHeader label="Dias ativos" field="active_days_total" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} align="right" />
                        </tr></thead>
                        <tbody className="divide-y divide-gray-100">
                            {rows.map((r) => {
                                const key = `${r.domain ?? ''}-${r.lead_id}`;
                                const sel = key === selKey;
                                return (
                                    <tr key={key} onClick={() => onSelectLead(r)}
                                        className={`cursor-pointer transition-colors ${sel ? 'bg-blue-50/70 border-l-2 border-blue-500' : 'hover:bg-gray-50/60'}`}>
                                        <td className="px-4 py-3">
                                            <p className="font-medium text-gray-900 leading-tight">{r.lead_nome || '—'}</p>
                                            <p className="text-[11px] text-gray-400 mt-0.5">#{r.lead_id}</p>
                                        </td>
                                        <td className="px-4 py-3"><CountryBadge code={r.lead_pais} /></td>
                                        {showDomain && <td className="px-4 py-3 text-gray-600 max-w-[140px] truncate">{r.domain || '—'}</td>}
                                        <td className="px-4 py-3 text-right font-semibold text-emerald-600">{money(r.revenue_lead)}</td>
                                        <td className="px-4 py-3 text-right text-gray-600">{num(r.clicks)}</td>
                                        <td className="px-4 py-3 text-right text-gray-600">{num(r.active_days_total ?? 0)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    <div className="px-4 pb-3"><Pagination total={total} limit={limit} offset={offset} onOffset={onOffset} /></div>
                </div>
            )}
        </div>
    );
}

function LeadDetail({ lead, fallbackDomain, start_date, end_date }: {
    lead: LtvLead | null; fallbackDomain: string | null; start_date: string; end_date: string;
}) {
    if (!lead) {
        return (
            <div className="bg-white border border-gray-200 rounded-2xl p-10 flex flex-col items-center justify-center text-center min-h-[240px]">
                <Activity className="w-8 h-8 text-gray-200 mb-2" />
                <p className="text-sm text-gray-400">Selecione um lead pra ver o mapa de dias e o tráfego dele.</p>
            </div>
        );
    }
    const scopeDomain = lead.domain || fallbackDomain || undefined;
    // mapa de dias: pelo menos 4 semanas terminando no fim do período
    const mapEnd = end_date;
    const mapStart = minIso(start_date, addDays(end_date, -27));
    return (
        <div className="bg-white border border-gray-200 rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-1">
                <h3 className="text-lg font-bold text-gray-900">{lead.lead_nome || '—'}</h3>
                <CountryBadge code={lead.lead_pais} />
            </div>
            <p className="text-xs text-gray-400 mb-4">#{lead.lead_id} · {scopeDomain || '—'} · {start_date} → {end_date}</p>
            <div className="grid grid-cols-5 gap-3 mb-5">
                <Stat2 label="Receita" value={money(lead.revenue_lead)} accent />
                <Stat2 label="Acessos" value={num(lead.pv_lead)} />
                <Stat2 label="Sessões" value={num(lead.sessions_lead)} />
                <Stat2 label="Clicks" value={num(lead.clicks)} />
                <Stat2 label="Dias ativos (total)" value={num(lead.active_days_total ?? 0)} />
            </div>
            <LeadDayMap leadId={String(lead.lead_id)} domain={scopeDomain} start_date={mapStart} end_date={mapEnd} />
            <LeadTraffic leadId={String(lead.lead_id)} start_date={start_date} end_date={end_date} />
        </div>
    );
}

const DAY_METRICS = [
    { key: 'revenue' as const, label: 'Receita', rgb: '16,185,129' },
    { key: 'pageviews' as const, label: 'Acessos', rgb: '59,130,246' },
    { key: 'clicks' as const, label: 'Clicks', rgb: '139,92,246' },
];
type DayMetric = 'revenue' | 'pageviews' | 'clicks';

function LeadDayMap({ leadId, domain, start_date, end_date }: {
    leadId: string; domain?: string; start_date: string; end_date: string;
}) {
    const [open, setOpen] = useState(false);
    const [metric, setMetric] = useState<DayMetric>('clicks');
    // Só busca quando expande. Receita/acessos escopados por domínio (índice
    // domain_lead_date, rápido); clicks são totais do lead (button_clicks por lead).
    const { data, isLoading } = useQuery({
        queryKey: ['ltv-lead-daily', leadId, domain ?? '', start_date, end_date],
        queryFn: () => getLtvLeadDaily(leadId, { start_date, end_date, domain: domain || undefined, with_paths: true }),
        enabled: open,
        staleTime: 60000,
    });
    const days: LtvLeadDay[] = data?.data ?? [];
    const m = DAY_METRICS.find((x) => x.key === metric)!;

    return (
        <div className="mb-4">
            <button onClick={() => setOpen((o) => !o)}
                className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-500 uppercase tracking-wide hover:text-gray-700 transition-colors">
                <CalendarDays className="w-3.5 h-3.5 text-blue-500" /> Mapa de dias
                {data?.meta?.active_days_total != null && (
                    <span className="text-blue-600 normal-case font-medium">· {num(data.meta.active_days_total)} dias ativos</span>
                )}
                {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
            {open && (
                <div className="mt-2">
                    <div className="flex items-center justify-end mb-1.5">
                        <div className="flex items-center bg-gray-100 rounded-lg p-0.5 gap-0.5">
                            {DAY_METRICS.map((x) => (
                                <button key={x.key} onClick={() => setMetric(x.key)}
                                    className={`px-2 py-1 rounded-md text-[11px] font-medium transition-all ${metric === x.key ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}>
                                    {x.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    {isLoading && !data ? (
                        <div className="h-28 rounded-lg bg-gray-50 animate-pulse" />
                    ) : days.length === 0 ? (
                        <p className="text-xs text-gray-400 py-6 text-center">Sem atividade no período.</p>
                    ) : (
                        <DayLineChart days={days} metric={metric} rgb={m.rgb} />
                    )}
                    <p className="text-[10px] text-gray-400 mt-1.5">{start_date} → {end_date} · {m.label} por dia — atividade total do lead</p>
                </div>
            )}
        </div>
    );
}

// Catmull-Rom → Bézier (curva suave), com clamp vertical pra não estourar o gráfico.
function smoothPath(pts: readonly (readonly [number, number])[], top: number, base: number): string {
    if (pts.length === 0) return '';
    if (pts.length === 1) return `M${pts[0][0]},${pts[0][1]}`;
    const clampY = (v: number) => Math.max(top, Math.min(base, v));
    const d = [`M${pts[0][0].toFixed(1)},${pts[0][1].toFixed(1)}`];
    for (let i = 0; i < pts.length - 1; i++) {
        const p0 = pts[i - 1] ?? pts[i];
        const p1 = pts[i];
        const p2 = pts[i + 1];
        const p3 = pts[i + 2] ?? p2;
        const c1x = p1[0] + (p2[0] - p0[0]) / 6;
        const c1y = clampY(p1[1] + (p2[1] - p0[1]) / 6);
        const c2x = p2[0] - (p3[0] - p1[0]) / 6;
        const c2y = clampY(p2[1] - (p3[1] - p1[1]) / 6);
        d.push(`C${c1x.toFixed(1)},${c1y.toFixed(1)} ${c2x.toFixed(1)},${c2y.toFixed(1)} ${p2[0].toFixed(1)},${p2[1].toFixed(1)}`);
    }
    return d.join(' ');
}

function DayLineChart({ days, metric, rgb }: { days: LtvLeadDay[]; metric: DayMetric; rgb: string }) {
    const [hover, setHover] = useState<{ i: number; x: number; y: number } | null>(null);
    const W = 320, H = 112, padX = 4, padTop = 10, padBottom = 8;
    const n = days.length;
    const max = Math.max(1, ...days.map((d) => d[metric]));
    const innerW = W - padX * 2;
    const innerH = H - padTop - padBottom;
    const base = padTop + innerH;
    const x = (i: number) => padX + (n <= 1 ? innerW / 2 : (i / (n - 1)) * innerW);
    const y = (v: number) => padTop + innerH - (v / max) * innerH;
    const pts = days.map((d, i) => [x(i), y(d[metric])] as const);
    const line = smoothPath(pts, padTop, base);
    const area = `${line} L${pts[n - 1][0].toFixed(1)},${base} L${pts[0][0].toFixed(1)},${base} Z`;
    const color = `rgb(${rgb})`;
    const gid = `dlc-${rgb.replace(/,/g, '-')}-${metric}`;
    const fmt = (d: LtvLeadDay) => (metric === 'revenue' ? money(d.revenue) : num(d[metric]));
    const maxDay = days.reduce((a, b) => (b[metric] > a[metric] ? b : a));

    const onMove = (e: React.MouseEvent<HTMLDivElement>) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const frac = (e.clientX - rect.left) / Math.max(1, rect.width);
        const i = Math.min(n - 1, Math.max(0, Math.round(frac * (n - 1))));
        setHover({ i, x: e.clientX, y: e.clientY });
    };
    const hd = hover ? days[hover.i] : null;

    // posiciona o tooltip dentro da tela: vira pra esquerda perto da borda direita
    const TIP_W = 300;
    let tipLeft = 0, tipTop = 0;
    if (hover && hd) {
        const nLinks = metric === 'pageviews' ? Math.min(8, hd.paths?.length ?? 0) : 0;
        const estH = 52 + nLinks * 20;
        const vw = typeof window !== 'undefined' ? window.innerWidth : 1280;
        const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
        tipLeft = hover.x + 16 + TIP_W > vw ? Math.max(8, hover.x - TIP_W - 16) : hover.x + 16;
        tipTop = Math.max(8, Math.min(hover.y + 14, vh - estH - 8));
    }

    return (
        <div>
            <div className="relative" style={{ height: H }} onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
                <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none" className="block">
                    <defs>
                        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={color} stopOpacity={0.18} />
                            <stop offset="100%" stopColor={color} stopOpacity={0} />
                        </linearGradient>
                    </defs>
                    <path d={area} fill={`url(#${gid})`} />
                    <path d={line} fill="none" stroke={color} strokeWidth={2} vectorEffect="non-scaling-stroke" strokeLinejoin="round" strokeLinecap="round" />
                    {hover && <line x1={pts[hover.i][0]} y1={padTop} x2={pts[hover.i][0]} y2={base} stroke={color} strokeOpacity={0.25} strokeWidth={1} vectorEffect="non-scaling-stroke" />}
                </svg>
                {hover && (
                    <div className="absolute w-2.5 h-2.5 rounded-full border-2 border-white shadow"
                        style={{ backgroundColor: color, left: `${(pts[hover.i][0] / W) * 100}%`, top: `${(pts[hover.i][1] / H) * 100}%`, transform: 'translate(-50%,-50%)', pointerEvents: 'none' }} />
                )}
            </div>
            <div className="flex items-center justify-between text-[9px] text-gray-300 mt-0.5 px-0.5">
                <span>{days[0].date.slice(5)}</span>
                <span className="text-gray-400">máx {fmt(maxDay)}</span>
                <span>{days[n - 1].date.slice(5)}</span>
            </div>
            {hover && hd && (
                <div className="fixed z-50 pointer-events-none bg-gray-900 text-white text-[11px] rounded-lg px-3 py-2 shadow-xl"
                    style={{ left: tipLeft, top: tipTop, width: TIP_W }}>
                    <p className="font-semibold">{hd.date}</p>
                    {metric === 'revenue' && <p className="text-emerald-300 mt-0.5">Receita: {money(hd.revenue)}</p>}
                    {metric === 'clicks' && <p className="text-violet-300 mt-0.5">Clicks: {num(hd.clicks)}</p>}
                    {metric === 'pageviews' && (
                        <div className="mt-0.5">
                            <p className="text-blue-300">Acessos: {num(hd.pageviews)}</p>
                            {hd.paths && hd.paths.length > 0 ? (
                                <div className="mt-1 pt-1 border-t border-white/10 space-y-1">
                                    {hd.paths.map((p, k) => (
                                        <div key={k} className="flex gap-1.5 leading-snug">
                                            <span className="text-gray-400 shrink-0">{p.pageviews}×</span>
                                            <span className="text-gray-200 break-all">{p.path || '—'}</span>
                                        </div>
                                    ))}
                                </div>
                            ) : hd.pageviews > 0 ? <p className="text-gray-500 mt-1">(links não registrados)</p> : null}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function Stat2({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
    return (
        <div>
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">{label}</p>
            <p className={`text-base font-bold mt-0.5 ${accent ? 'text-emerald-600' : 'text-gray-900'}`}>{value}</p>
        </div>
    );
}

function LeadTraffic({ leadId, start_date, end_date }: { leadId: string; start_date: string; end_date: string }) {
    const { data, isLoading } = useQuery({
        queryKey: ['ltv-lead-traffic', leadId, start_date, end_date],
        queryFn: () => getLtvLeadTraffic(leadId, { start_date, end_date }),
        staleTime: 60000,
    });
    const rows = data?.data ?? [];
    const totalPv = data?.meta?.total_pageviews ?? 0;
    return (
        <div>
            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                <Activity className="w-3.5 h-3.5 text-blue-500" /> Tráfego do lead
                <span className="text-gray-300 font-normal normal-case">· {num(totalPv)} pageviews no período</span>
            </p>
            {isLoading && !data ? (
                <div className="py-6"><Spinner label="Carregando tráfego..." /></div>
            ) : rows.length === 0 ? (
                <p className="text-sm text-gray-400 py-2">Sem tráfego registrado pra esse lead no período.</p>
            ) : (
                <div className="border border-gray-100 rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                        <thead><tr className="border-b border-gray-100 bg-gray-50/50">
                            <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Caminho (URL)</th>
                            <th className="px-3 py-2 text-right text-[10px] font-semibold text-gray-400 uppercase tracking-wide">PV</th>
                            <th className="px-3 py-2 text-left text-[10px] font-semibold text-gray-400 uppercase tracking-wide w-32">Participação</th>
                        </tr></thead>
                        <tbody className="divide-y divide-gray-50">
                            {rows.map((t, i) => {
                                const share = totalPv ? (t.pageviews / totalPv) * 100 : 0;
                                return (
                                    <tr key={`${t.domain ?? ''}-${t.path ?? i}`} className="hover:bg-gray-50/60">
                                        <td className="px-3 py-2 max-w-[220px]">
                                            <p className="font-mono text-[12px] text-gray-700 truncate" title={t.url || t.path || ''}>{t.path || t.url || '(sem caminho)'}</p>
                                        </td>
                                        <td className="px-3 py-2 text-right font-medium text-gray-700">{num(t.pageviews)}</td>
                                        <td className="px-3 py-2">
                                            <div className="flex items-center gap-2">
                                                <div className="flex-1 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                                                    <div className="h-full rounded-full bg-blue-500" style={{ width: `${Math.max(2, share)}%` }} />
                                                </div>
                                                <span className="text-[11px] text-gray-400 w-8 text-right">{Math.round(share)}%</span>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
